import { createContext, useContext, useState, useEffect } from 'react';

// Definição dos esquemas de cor
const colorSchemes = {
  default: {
    primary: 'hsl(var(--primary))',
    secondary: 'hsl(var(--secondary))',
    accent: 'hsl(var(--accent))',
  },
  blue: {
    primary: 'hsl(210, 100%, 50%)',
    secondary: 'hsl(210, 100%, 40%)',
    accent: 'hsl(210, 100%, 60%)',
  },
  green: {
    primary: 'hsl(142, 76%, 36%)',
    secondary: 'hsl(142, 76%, 26%)',
    accent: 'hsl(142, 76%, 46%)',
  },
  purple: {
    primary: 'hsl(270, 100%, 50%)',
    secondary: 'hsl(270, 100%, 40%)',
    accent: 'hsl(270, 100%, 60%)',
  },
  orange: {
    primary: 'hsl(24, 100%, 50%)',
    secondary: 'hsl(24, 100%, 40%)',
    accent: 'hsl(24, 100%, 60%)',
  },
};

type ColorScheme = keyof typeof colorSchemes;

interface ThemeContextType {
  colorScheme: ColorScheme;
  setColorScheme: (scheme: ColorScheme) => void;
  themeMode: 'light' | 'dark';
  setThemeMode: (mode: 'light' | 'dark') => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [colorScheme, setColorScheme] = useState<ColorScheme>(() => {
    const savedScheme = localStorage.getItem('colorScheme') as ColorScheme;
    return savedScheme || 'default';
  });
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('themeMode');
    if (saved === 'dark' || saved === 'light') return saved;
    // Detecta preferência do sistema
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  useEffect(() => {
    localStorage.setItem('colorScheme', colorScheme);
    const colors = colorSchemes[colorScheme];
    Object.entries(colors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--${key}`, value);
    });
  }, [colorScheme]);

  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
    if (themeMode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [themeMode]);

  return (
    <ThemeContext.Provider value={{ colorScheme, setColorScheme, themeMode, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
